import { useEffect, useMemo, useState } from "react";
import { Controller, FieldError, FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";
import * as Yup from "yup";

import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Header from "components/Header";
import Input from "components/Input";
import Modal from "components/Modal";
import Select from "components/Select";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { POST_TO } from "constants/loads";
import { routes } from "constants/routes";
import { EQUIPMENT_TYPES } from "constants/signup";
import { QUERY_LOAD_DETAIL } from "features/main/Loads/Detail/query";
import OriginInputGroup from "features/main/Loads/modules/OriginInputGroup";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { ICreateLoadForm, IDestinationLocation, ILocations, IOriginLocation } from "models/Load";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { selectFactoringProfile } from "store/user/selector";
import DestinationInputGroup from "../../modules/DestinationInputGroup";
import { MUTATION_CREATE_LOAD, MUTATION_UPDATE_LOAD } from "./query";
import styles from "./style.module.scss";
import deepClone from "common/deepClone";
import DollarInput from "components/DollarInput";

const createLoadFormSchema = (autoGenerateLoadNumber: boolean) =>
  Yup.object<Partial<ICreateLoadForm>>({
    originLocations: Yup.array().of(
      Yup.object({
        origin: Yup.object({
          city: Yup.string().required("Must include both city and state."),
          state: Yup.string().required("Must include both city and state.")
        }).required("Must include both city and state.")
      })
    ),
    destinationLocations: Yup.array().of(
      Yup.object({
        destination: Yup.object({
          city: Yup.string().required("Must include both city and state."),
          state: Yup.string().required("Must include both city and state.")
        }).required("Must include both city and state.")
      })
    ),
    userLoadNumber: Yup.string().when([], {
      is: () => !autoGenerateLoadNumber,
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired()
    }),
    equipmentType: Yup.object().required("This field is required."),
    targetRate: Yup.string().required("This field is required."),
    count: Yup.number()
      .transform((val, orig) => (orig === "" ? undefined : val))
      .integer("Count must be an integer")
      .max(9, "Count must be less than or equal to 9")
      .moreThan(0, "Count must be greater than or equal to 1"),
    loadWeight: Yup.number()
      .typeError("This field is required.")
      .required("This field is required.")
      .integer("Weight must be an integer")
      .moreThan(99, "Weight must be greater than or equal to 100 lbs."),
    loadLength: Yup.number()
      .typeError("This field is required.")
      .required("This field is required.")
      .integer("Length must be an integer")
      .moreThan(1, "Length must be greater than 1 ft.")
      .max(100, "Length must be less than or equal to 100 ft."),
    pickUpOpenDate: Yup.date()
      .required("This field is required")
      .min(dayjs().subtract(1, "day"), "Date is Too early."),
    pickUpCloseDate: Yup.date()
      .required("This field is required")
      .min(Yup.ref("pickUpOpenDate"), "Must be no earlier than Pick up close"),
    deliveryByDate: Yup.date()
      .required("This field is required")
      .min(Yup.ref("pickUpCloseDate"), "Must be no earlier than Deliver by")
  });

export default function LoadPostCreate() {
  const { postedId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isBroker } = useUserFeature();
  const factoringProfile = useSelector(selectFactoringProfile);

  const [createdLoad, setCreatedLoad] = useState<{
    id: string;
    length: number;
    userLoadNumber: string;
    targetRate: number;
  }>();
  const [pickUpOpenDate, setPickUpOpenDate] = useState(dayjs().format("MM/DD/YYYY HH:mm"));
  const [pickUpCloseDate, setPickUpCloseDate] = useState(dayjs().format("MM/DD/YYYY HH:mm"));
  const [deliveryByDate, setDeliveryByDate] = useState(dayjs().format("MM/DD/YYYY HH:mm"));
  const [isFundingRequestModalOpen, toggleFundingRequestModalOpen] = useToggle();
  const validationSchema = useMemo(() => {
    if (factoringProfile) return createLoadFormSchema(factoringProfile?.auto_generate_load_numbers);
    else return createLoadFormSchema(true);
  }, [factoringProfile]);

  const defaultValue = {
    originLocations: [
      {
        origin: null,
        originName: "",
        originInstruction: ""
      }
    ],
    destinationLocations: [
      {
        destination: null,
        destinationName: "",
        destinationInstruction: ""
      }
    ],
    count: "1",
    pickUpOpenDate: dayjs().format("MM/DD/YYYY HH:mm"),
    pickUpCloseDate: dayjs().add(1, "day").format("MM/DD/YYYY HH:mm"),
    deliveryByDate: dayjs().add(4, "day").format("MM/DD/YYYY HH:mm"),
    postTo: isBroker ? POST_TO[1][0] : POST_TO[0][0]
  };

  const methods = useForm<Partial<ICreateLoadForm>>({
    mode: "all",
    resolver: yupResolver(validationSchema),
    values: defaultValue
  });

  const [CreateLoad] = useMutation(MUTATION_CREATE_LOAD);

  const [UpdateLoad] = useMutation(MUTATION_UPDATE_LOAD);

  const goBack = () => navigate(routes.LOADS_POSTED_ALL);

  const { data, loading, error } = useQuery(QUERY_LOAD_DETAIL, {
    variables: { id: postedId },
    skip: !postedId,
    fetchPolicy: "cache-and-network"
  });

  useEffect(() => {
    dispatch(handleLoading(loading));

    if (error) showErrorMessage(error.message);
  }, [loading, error]);

  useEffect(() => {
    if (data?.load && postedId) {
      const selectedLoad = data.load;
      const originArr: ILocations[] = JSON.parse(selectedLoad.originLocations);
      const originLocationArr = originArr.map((orig) => ({
        origin: {
          street_one: orig.street_one,
          city: orig.city,
          state: orig.state,
          zip: orig.zip,
          country: orig.country
        },
        originName: orig.name,
        originInstruction: orig.instructions
      })) as IOriginLocation[];

      const destinationArr: ILocations[] = JSON.parse(selectedLoad.destinationLocations);
      const destinationLocationArr = destinationArr.map((destination) => ({
        destination: {
          street_one: destination.street_one,
          city: destination.city,
          state: destination.state,
          zip: destination.zip,
          country: destination.country
        },
        destinationName: destination.name,
        destinationInstruction: destination.instructions
      })) as IDestinationLocation[];

      methods.reset({
        originLocations: originLocationArr,
        destinationLocations: destinationLocationArr,
        loadWeight: selectedLoad.weight,
        loadLength: selectedLoad.length,
        equipmentType: EQUIPMENT_TYPES.find(({ value }) => value === selectedLoad.trailerType),
        targetRate: selectedLoad.targetRate,
        userLoadNumber: selectedLoad.userLoadNumber,
        pickUpOpenDate: dayjs(selectedLoad.timeStart).format("MM/DD/YYYY HH:mm"),
        pickUpCloseDate: dayjs(selectedLoad.timeEnd).format("MM/DD/YYYY HH:mm"),
        deliveryByDate: dayjs(selectedLoad.timeDelivery).format("MM/DD/YYYY HH:mm"),
        moreDetails: selectedLoad.details,
        postTo: isBroker
          ? POST_TO[1].find(({ value }) => value === selectedLoad.groupsPostTo)
          : POST_TO[0].find(({ value }) => value === selectedLoad.postTo)
      });
    }
  }, [data, postedId]);

  const onSubmit = async (fields: ICreateLoadForm) => {
    const data = deepClone(fields);
    const {
      originLocations,
      destinationLocations,
      loadWeight,
      loadLength,
      equipmentType,
      targetRate,
      userLoadNumber,
      pickUpOpenDate,
      pickUpCloseDate,
      deliveryByDate,
      moreDetails,
      postTo
    } = data;

    const originLocationArr = originLocations.map((item) => ({
      name: item.originName,
      streetOne: item.origin?.address,
      city: item.origin?.city,
      state: item.origin?.state,
      country: item.origin?.country,
      instructions: item.originInstruction
    }));

    const destinationLocationArr = destinationLocations.map((item) => ({
      name: item.destinationName,
      streetOne: item.destination?.address,
      city: item.destination?.city,
      state: item.destination?.state,
      country: item.destination?.country,
      instructions: item.destinationInstruction
    }));

    const submitData = {
      originLocations: originLocationArr,
      destinationLocations: destinationLocationArr,
      trailerType: equipmentType.value,
      targetRate: targetRate * 100,
      weight: loadWeight,
      length: loadLength,
      timeStart: dayjs(pickUpOpenDate).format("YYYY-MM-DDTHH:mm:ss"),
      timeEnd: dayjs(pickUpCloseDate).format("YYYY-MM-DDTHH:mm:ss"),
      timeDelivery: dayjs(deliveryByDate).format("YYYY-MM-DDTHH:mm:ss"),
      details: moreDetails,
      postTo: isBroker ? "all" : postTo.value,
      userLoadNumber: userLoadNumber ?? "",
      groupsPostTo: isBroker ? postTo.value : "all"
    };

    try {
      dispatch(handleLoading(true));

      if (!postedId) {
        try {
          const res = await CreateLoad({
            variables: { ...submitData }
          });
          setCreatedLoad(res.data.createLoad.load);
          showSuccessMessage("Successfully post a load.");
        } catch {
          showErrorMessage("Error posting a load");
          return;
        }
      } else {
        try {
          await UpdateLoad({
            variables: { id: postedId, ...submitData }
          });
          showSuccessMessage("Successfully update a load.");
        } catch {
          showErrorMessage("Error updating a load.");
          return;
        }
      }

      if (isBroker && !postedId && factoringProfile?.factoring_approved === "approved") {
        toggleFundingRequestModalOpen();
      } else {
        goBack();
      }
    } finally {
      dispatch(handleLoading(false));
    }
  };

  function goToFundingRequest() {
    toggleFundingRequestModalOpen();
    navigate(routes.LOADS_CREATE_FUNDING_REQUEST, {
      state: {
        loadId: createdLoad?.id,
        loadLength: createdLoad?.length,
        userLoadNumber: createdLoad?.userLoadNumber,
        targetRate: createdLoad?.targetRate,
        isOriginLoad: false
      }
    });
  }

  function goToPostedLoad() {
    toggleFundingRequestModalOpen();
    goBack();
  }

  const {
    control,
    register,
    formState: { errors, isSubmitting },
    handleSubmit
  } = methods;

  return (
    <MainLayout
      desktopTitle={postedId ? "Edit Load" : "Post Load"}
      className={styles.loadPostCreateContainer}
      customHeader={
        <Header
          label={postedId ? "Edit Load" : "Post Load"}
          goBackPath={routes.LOADS}
          isHideDesktop
        />
      }>
      <FormProvider {...methods}>
        <form className={styles.formData} onSubmit={handleSubmit(onSubmit)}>
          <OriginInputGroup />

          <DestinationInputGroup />

          <div className={styles.destinationFormGroup}>
            {factoringProfile && !factoringProfile?.auto_generate_load_numbers ? (
              <Input
                label="&nbsp;*Load Number"
                name="userLoadNumber"
                className="flex-1"
                register={register}
                error={errors.userLoadNumber}
              />
            ) : null}
            <Input
              type="number"
              inputMode="numeric"
              label="&nbsp;*Load Weight (Max)"
              placeholder="45000"
              name="loadWeight"
              className="flex-1"
              register={register}
              error={errors.loadWeight}
              suffixUnit="lbs"
            />
            <Input
              type="number"
              inputMode="numeric"
              label="&nbsp;*Load Length"
              placeholder="53"
              name="loadLength"
              className="flex-1"
              register={register}
              error={errors.loadLength}
              suffixUnit="&nbsp;Ft&nbsp;"
            />
          </div>
          <div className={styles.originFormGroup}>
            <Controller
              name="equipmentType"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  value={value}
                  placeholder="Equipment Type"
                  onChange={onChange}
                  onBlur={onBlur}
                  options={EQUIPMENT_TYPES}
                  label="&nbsp;*Equipment Type"
                  className="flex-1"
                  error={errors.equipmentType as FieldError}
                />
              )}
            />
            <DollarInput
              name="targetRate"
              label="&nbsp;*Target Rate"
              className="flex-1 target-rate"
              error={errors.targetRate}
            />
            {postedId === undefined && (
              <Input
                type="number"
                inputMode="numeric"
                label="&nbsp;*Post Count"
                className="flex-1"
                name="count"
                register={register}
                error={errors.count}
              />
            )}
          </div>
          <div className={styles.originFormGroup}>
            <Controller
              name="pickUpOpenDate"
              control={control}
              render={({ field: { value = pickUpOpenDate, onChange } }) => (
                <DatePicker
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setPickUpOpenDate(value);
                  }}
                  label="&nbsp;Pick Up Open"
                  placeholder="Select Date & Time"
                  error={errors.pickUpOpenDate}
                  className="flex-1"
                  showTimePicker
                />
              )}
            />
            <Controller
              name="pickUpCloseDate"
              control={control}
              render={({ field: { value = pickUpCloseDate, onChange } }) => (
                <DatePicker
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setPickUpCloseDate(value);
                  }}
                  label="&nbsp;Pick Up Close"
                  placeholder="Select Date & Time"
                  error={errors.pickUpCloseDate}
                  className="flex-1"
                  showTimePicker
                />
              )}
            />
            <Controller
              name="deliveryByDate"
              control={control}
              render={({ field: { value = deliveryByDate, onChange } }) => (
                <DatePicker
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setDeliveryByDate(value);
                  }}
                  label="&nbsp;Deliver By"
                  placeholder="Select Date & Time"
                  error={errors.deliveryByDate}
                  className="flex-1"
                  showTimePicker
                />
              )}
            />
          </div>
          <div className={styles.originFormGroup}>
            <Input
              label={
                <>
                  <span className="pl-1">More Details - </span>
                  <span className="italic text-xxs">Optional</span>
                </>
              }
              name="moreDetails"
              placeholder="More details, commodity, or dimensions here"
              className="flex-1"
              register={register}
            />
            <Controller
              name="postTo"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value}
                  onChange={onChange}
                  options={isBroker ? POST_TO[1] : POST_TO[0]}
                  label="&nbsp;Post To"
                  direction="up"
                  className="flex-1"
                />
              )}
            />
          </div>
          <div className={styles.footer}>
            <Button size="lg" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Icon path={mdiLoading} size={1.5} spin={1} />
              ) : postedId ? (
                "Repost Load"
              ) : (
                "Post Load"
              )}
            </Button>
          </div>
        </form>
      </FormProvider>

      <Modal
        isOpen={isFundingRequestModalOpen}
        closeModal={toggleFundingRequestModalOpen}
        wrapperClass="!my-auto desktop:!min-h-fit text-sm"
        dialogClass="!bg-foreground !px-3 !py-4 !border !border-card !overflow-y-auto gap-2.5"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <div className="flex items-center gap-1 text-xl font-bold text-primary">
          <ExclamationSVGIcon />
          <span>Confirm</span>
        </div>
        <div className="pt-1.5 font-medium">
          <p>
            Would you like to assign a carrier from your group to this load now and create a pending
            carrier payment?
          </p>
        </div>
        <div className="flex gap-4 justify-end *:w-20 *:text-sm *:rounded-full *:h-9">
          <Button onClick={goToPostedLoad} variant="outline" className="!w-32">
            No Thanks!
          </Button>
          <Button onClick={goToFundingRequest}>Yes</Button>
        </div>
      </Modal>
    </MainLayout>
  );
}
